import React, { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Button,
  Box,
  ContentTemplate,
  ErrorInfo,
  Grid,
  HtmlTrans,
  Icon,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { useParams, Prompt } from 'react-router-dom';
import RouteKey from '../../../../../../routes/key';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { attachedFilesService, candidateService } from '../../../../../../api';
import { prepareFiles } from '../../../../../../utils/UploadUtil';
import useSetUploadButtonStatus from '../../../../../../hooks/useSetUploadButtonStatus';
import DraftTimeStamp from '../../../../../../components/DraftTimeStamp';
import { ApplicationType } from '../../../../../../api/candidate/config/model';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import UploadDocuments from '../../../../../../pages/accessArrangement/SelectCondition/components/UploadDocuments';
import SubmittedDetails from '../SubmittedDetails';
import { AttachedFileInfo } from '../../../../../../api/candidate/access-arrangement/model';
import { getIndividualReportFiles, getIndividualReportSubmittedFiles, getIndividualReportTemplate } from '../../../../../../api/candidate/access-arrangement/@candidate/api-services/api';

export const useStyles = makeStyles()((theme) => ({
  buttonContainer: {
    marginTop: theme.spacing(8),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  iconReminder: {
    marginRight: theme.spacing(1),
  },
  messageReminder: {
    fontWeight: 700,
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  a: {
    color: theme.color.link.linkDefault01,
  },
  downloadButton: {
    marginTop: theme.spacing(2),
  },
}));

interface IndividualReportContentProps {
  amendment: boolean;
  handleBack: () => void;
  onSuccess: () => void;
}

export enum FormIds {
  UPLOAD_DOCUMENTS_CONTAINER = 'upload-document-container',
}

export interface ErrorObject {
  id: FormIds;
  text: string;
}

const {
  ammendAccessArrangements,
  root,
  submitted: submittedRoute,
  accessArrangementsRequest,
  individualReport,
  reviewApplication,
  coordinatorRoot,
  coordinatorSubmitted,
  coordinatorReviewApplication,
  coordinatorIndividualReport,
  coordinatorAccessArrangementsRequest,
  coordinatorAmendAccessArrangements,
} = RouteKey['access-arrangement'].child;

const findError = (errorId: string, formErrors: ErrorObject[]) =>
  formErrors.find((formError) => formError.id === errorId);

const IndividualReportPage = ({ amendment, handleBack, onSuccess }: IndividualReportContentProps) => {
  const { t } = useTranslation();
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [formErrors] = useState<ErrorObject[]>([]);
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { uuid } = useParams<{ uuid: string }>();
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [submittedFiles] = useState(0);
  const [allowProceed, setAllowProceed] = useState(false);
  const [downloadTemplateStatus, setDownloadTemplateStatus] = useState<MutationStatus>('idle');
  const [submittedData, setSubmittedData] = useState<AttachedFileInfo[]>();

  // Load existing individual report draft files on page load
  useEffect(() => {
    getIndividualReportFiles(uuid)
      .then((res) => {
        const existingFiles = res.data;
        if (existingFiles && existingFiles.length > 0) {
          const mapped: UploadedFile[] = existingFiles.map((draftFile) => ({
            file: new File([new ArrayBuffer(draftFile.fileSizeInBytes)], draftFile.fileName),
            hasError: false,
            pending: false,
            uploaded: true,
            key: draftFile.fileUuid,
          }));
          setDraftFiles(mapped);
        }
      })
      .catch(() => {});
  }, [uuid]);

  // For amendment: load submitted individual report files
  useEffect(() => {
    if (!amendment) return;
    getIndividualReportSubmittedFiles(uuid)
      .then((res) => {
        const submittedFiles = res.data;
        if (submittedFiles && submittedFiles.length > 0) {
          setSubmittedData(submittedFiles);
        }
      })
      .catch(() => {});
  }, [uuid, amendment]);

  const downloadTemplate = () => {
    setDownloadTemplateStatus('loading');
    getIndividualReportTemplate()
      .then((res) => {
        setDownloadTemplateStatus('idle');
        window.open(res.data.url, '_blank');
      })
      .catch(() => {
        setDownloadTemplateStatus('error');
      });
  };

  const {
    data: timestampData,
    error: timestampError,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementDraftDateTime(uuid);

  const postRequestS3PresignLinks =
    candidateService.accessArrangement.candidate.usePostAppealArrangementUploadDocuments(
      ({ data }) => {
        const newFiles: UploadedFile[] = [];
        files.forEach((_item, index) => {
          const pendingFile = files[index];
          const fileData = data[index];
          pendingFile.presignedUrl = fileData.presignedUrl;
          pendingFile.key = fileData.key;
          newFiles.push(pendingFile);
        });
        setFiles(newFiles);
        setShouldSubmit(true);
      },
      (err) => {
        setApiButtonStatus('error');
        setErrorMsg(err?.response?.data?.message ?? 'Upload failed. Please try again.');
      }
    );

  useSetUploadButtonStatus({
    files,
    setApiButtonStatus,
    setShouldSubmit,
    shouldSubmit,
    onSuccessCallback: onSuccess,
  });

  attachedFilesService.all.useDeleteAppealArrangementFile(() => null);


  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleConfirm = () => {
    // Check no-amendment: if in amendment mode, no new files added, and session says noAmendment
    if (
      amendment &&
      files.length < 1 &&
      draftFiles.length < 1 &&
      sessionStorage.getItem(uuid) === 'noAmendment'
    ) {
      setErrorMsg(t('accessArrangement->>AccessArrangementRequest.NoAmendmentError'));
      return;
    }
    if (!amendment && files.length < 1 && draftFiles.length < 1) {
      setDisplayErrors(true);
      return;
    }
    setAllowProceed(true);
    if (files.length > 0) {
      const { filenames } = prepareFiles(files, setFiles);
      sessionStorage.removeItem(uuid);
      postRequestS3PresignLinks.mutate({
        payload: {
          applicationType: 'ACCESS_ARRANGEMENT',
          uuid,
          filenames,
          uploadDocumentType: 'INDIVIDUAL_REPORT',
        },
        uuid,
      });
    } else {
      onSuccess();
    }
  };

  if (timestampError) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const uploadDocumentsContainerError = findError(FormIds.UPLOAD_DOCUMENTS_CONTAINER, formErrors);

  return (
    <>
    <Prompt
      when={!allowProceed}
      message={(location) => {
        const nextPath = location.pathname;

        if (
          nextPath.includes(reviewApplication.route) ||
          nextPath.includes(coordinatorReviewApplication.route)
        ) {
          return true;
        }
        return t("accessArrangement->>AccessArrangements.ReminderToSubmitPopUp");
      }}
    />
      <ContentTemplate isDividerBottom isContinuable>
        <SectionHeader title={t('accessArrangement->>IndividualReport.IndividualReport')} />
        <Typography>{t('accessArrangement->>IndividualReport.RelevantInformation1')}</Typography>
        <br />
        <Typography>
          <HtmlTrans
            i18nKey="accessArrangement->>IndividualReport.RelevantInformation2"
            values={{ aClass: classes.a }}
          />
        </Typography>
        <br />
        <Typography>{t('accessArrangement->>IndividualReport.RelevantInformation3')}</Typography>
        {amendment && (
          <SubmittedDetails documents={submittedData} />
        )}
      </ContentTemplate>
      <ContentTemplate isDividerBottom isContinuable>
        <Box mt={-5}>
          <SectionHeader title={t('accessArrangement->>IndividualReport.DownloadIndividualReportTemplate')} />
          <Typography>{t('accessArrangement->>IndividualReport.DownloadIndividualReportTemplateDescription')}</Typography>
          <Box mt={2} mb={8}>
            <ApiButton
              id="button-download-template"
              className={classes.downloadButton}
              status={downloadTemplateStatus}
              variant="outlined"
              size="medium"
              onClick={() => downloadTemplate()}
            >
              <IconWithLabel
                iconName={IconName.download}
                iconSize="M"
                label={t('accessArrangement->>IndividualReport.DownloadExcelTemplate')}
                typographyProps={{ variant: 'h5', color: 'inherit' }}
              />
            </ApiButton>
          </Box>
        </Box>
      </ContentTemplate>
      <ContentTemplate continuous>
        <UploadDocuments
          isHeader
          files={files}
          setFiles={setFiles}
          hasError={uploadDocumentsContainerError && displayErrors}
          submittedFiles={submittedFiles}
          applicationType={ApplicationType.ACCESS_ARRANGEMENT_INDV_REPORT}
          draftFiles={draftFiles}
          setDraftFiles={setDraftFiles}
        />
        <ErrorInfo label={errorMsg} />
        <Grid xs={12} className={classes.buttonContainer}>
          {displayErrors && files.length < 1 && draftFiles.length < 1 && (
            <Typography variant="body2" className={classes.errString}>
              {t('accessArrangement->>IndividualReport.NoDocumentErrMessage')}
            </Typography>
          )}
          <Button
            onClick={() => { handleBack(); }}
            variant="outlined"
            className={classes.backButton}
            fullWidth={isMobile}
          >
            {t('accessArrangement->>IndividualReport.Back')}
          </Button>
          <ApiButton
            id="confirm-medical-condition-button"
            status={apiButtonStatus}
            variant="contained"
            fullWidth={isMobile}
            onClick={handleConfirm}
            disabled={errorMsg === t('accessArrangement->>AccessArrangementRequest.NoAmendmentError')}
          >
            {t('accessArrangement->>IndividualReport.ContinueToReviewApplication')}
          </ApiButton>
        </Grid>
        {timestampData && <DraftTimeStamp savedAt={timestampData} />}
        <Box display="flex" alignItems="center">
          <Icon
            size="M"
            name={IconName.info}
            className={classes.iconReminder}
            color={theme.color.icon.iconTrash}
          />
          <Typography className={classes.messageReminder} style={{ color: theme.color.icon.iconTrash }}>
            {t('components->>ApplicationReminderMessage.ReminderText')}
          </Typography>
        </Box>
      </ContentTemplate>
    </>
  );
};

export default IndividualReportPage;
